import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getImageUrl } from '../utils/imageHelper';
import Navbar from './Navbar';
import Footer from './Footer';
import BoardGameCoverPlaceholder from './BoardGameCoverPlaceholder';

const BoardGames = () => {
  const [boardGames, setBoardGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});

  useEffect(() => {
    fetchBoardGames();
  }, [page, search]);

  const fetchBoardGames = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(search && { search })
      });

      const response = await fetch(`/api/board-games?${params}`);
      const data = await response.json();

      if (data.success) {
        setBoardGames(data.data);
        setPagination(data.pagination);
      }
    } catch (err) {
      console.error('Error fetching board games:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchBoardGames();
  };

  return (
    <div className="min-h-screen bg-base-200">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-5xl font-bold mb-4">Board Games Database</h1>
          <p className="text-xl text-base-content/70">
            Discover and explore board games
          </p>
        </motion.div>

        {/* Search */}
        <div className="max-w-2xl mx-auto mb-8">
          <form onSubmit={handleSearch} className="flex gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search board games..."
              className="input input-bordered flex-1"
            />
            <button type="submit" className="btn btn-primary">
              Search
            </button>
          </form>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <span className="loading loading-spinner loading-lg"></span>
          </div>
        ) : (
          <>
            {/* Results Count */}
            <div className="mb-4 text-center">
              <p className="text-base-content/70">
                {pagination.total || 0} board games found
              </p>
            </div>

            {/* Board Games Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {boardGames.map((game, index) => (
                <motion.div
                  key={game.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={`/board-games/${game.slug}`}
                    className="card bg-base-100 shadow-lg hover:shadow-xl transition-all h-full"
                  >
                    <figure className="h-48 bg-base-300">
                      {game.coverImage ? (
                        <img
                          src={getImageUrl(game.coverImage)}
                          alt={game.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <BoardGameCoverPlaceholder />
                      )}
                    </figure>
                    <div className="card-body">
                      <h2 className="card-title text-lg">{game.name}</h2>
                      
                      <div className="text-sm space-y-1">
                        <p>
                          👥 {game.minPlayers}-{game.maxPlayers} players
                        </p>
                        {game.duration && <p>⏱️ {game.duration} min</p>}
                        {game.complexity && (
                          <p className="capitalize">📊 {game.complexity}</p>
                        )}
                      </div>

                      {game.description && (
                        <p className="text-sm text-base-content/70 line-clamp-2 mt-2">
                          {game.description}
                        </p>
                      )}

                      <div className="card-actions justify-between mt-4 text-xs text-base-content/70">
                        <span>{game._count?.clubBoardGames || 0} clubs</span>
                        <span>{game._count?.discussionLinks || 0} discussions</span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* Empty State */}
            {boardGames.length === 0 && (
              <div className="text-center py-20">
                <p className="text-xl text-base-content/70">No board games found</p>
              </div>
            )}

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex justify-center gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="btn btn-outline"
                >
                  Previous
                </button>
                <span className="btn btn-ghost">
                  Page {page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === pagination.pages}
                  className="btn btn-outline"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default BoardGames;
